import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPut, axiosPost, axiosDelete } from '../api/axios';
import { clientQueryKeys } from './useClients';

export const resourceQueryKeys = {
  resource: ['resource'] as const,
  resources: ['resources'] as const,
};

export interface Resource {
  challenge_areas?: number[] | null;
  client_id?: number;
  client_name?: string;
  domain_id?: number;
  domain?: { id: number; key: string; name: string };
  enabled_for?: Record<string, number> | null;
  description: string;
  id?: number;
  image_url?: string;
  wide_image_url?: string;
  long_description?: string;
  meta_keywords?: string;
  name: string;
  notes: string;
  phone_number: string;
  tag_names?: string[];
  resource_tags?: ResourceTag[];
  sort_weight: number;
  status: string;
  subject_areas?: number[] | null;
  tag_ids?: number[];
  text_number?: string;
  url_description?: string;
  url: string;
  is_pre_crisis: boolean;
}

export interface ResourceTag {
  topic_id?: number;
  topic?: Topic;
  type: string;
  id: number;
  resource_id: number;
  subdivision?: string;
  city?: string;
  country?: string;
  partner: any;
  partner_id: any;
}

export interface Topic {
  audio_file_path?: string;
  identifier: string;
  morphii_metadata: MorphiiMetadaum[];
  id: number;
  sample_2_theme: any;
  is_featured: boolean;
  sample_1_audio_file_path: string;
  morphii: string[];
  theme: string;
  button_image_selected_file_path: string;
  sample_2_audio_file_path: any;
  is_required: boolean;
  sort_weight: number;
  status: string;
  button_image_file_path: string;
  sample_1_theme?: string;
  name: string;
  description: string;
  tag_id: number;
  is_default: boolean;
  is_primary: boolean;
}

export interface MorphiiMetadaum {
  id: string;
  label: string;
  delighted: Delighted;
  disappointed: Disappointed;
}

export interface Delighted {
  polarity: number;
}

export interface Disappointed {
  polarity: number;
}

export type EnableFor = 'all' | { vertical_ids: number[] };

export interface ResourceV3CreatePayload {
  name: string;
  description: string;
  long_description?: string;
  meta_keywords?: string;
  phone_number?: string | null;
  url: string | null;
  url_description?: string | null;
  image_url?: string | null;
  wide_image_url?: string | null;
  status?: string;
  sort_weight?: number;
  notes?: string;
  client_id?: number | null;
  tag_ids?: number[];
  is_pre_crisis?: boolean;
  enable_for?: EnableFor | null;
}

export type ResourceV3UpdatePayload = Partial<ResourceV3CreatePayload>;

const getResources = async (hidden: boolean, search?: string, clientId?: number): Promise<Resource[]> => {
  const params: any = { client_id: clientId, status: hidden ? 'hidden' : 'live' };
  if (search) params['search'] = search;
  return await axiosGet('/resources', params, 'v2').then((response) => response.data);
};

const getResource = async (resourceId: number): Promise<Resource> => {
  return await axiosGet(`/resources/${resourceId}/`, null, 'v2').then((resourceResponse) => resourceResponse.data);
};

const addResourceRequest = async (data: any): Promise<Resource> => {
  return await axiosPost(`/resources/`, data, 'v2').then((response: { data: any }) => response.data);
};

const updateResourceRequest = async (resourceId: number, data: any): Promise<Resource> => {
  return await axiosPut(`/resources/${resourceId}/`, data, 'v2').then((response: { data: any }) => response.data);
};

const removeResourceRequest = async (resourceId: number): Promise<Resource> => {
  return await axiosDelete(`/resources/${resourceId}/`, null, 'v2').then((response: { data: any }) => response.data);
};

const addResourceV3Request = async (data: ResourceV3CreatePayload): Promise<Resource> => {
  return await axiosPost(`/resources/`, data, 'v3').then((response: { data: Resource }) => response.data);
};

const updateResourceV3Request = async (resourceId: number, data: ResourceV3UpdatePayload): Promise<Resource> => {
  return await axiosPut(`/resources/${resourceId}/`, data, 'v3').then((response: { data: Resource }) => response.data);
};

export interface ResourceBrowseParams {
  search?: string;
  admin_override?: boolean;
  tag_groups?: number[];
  tag_ids?: number[];
  domain_id?: number;
  client_id?: number;
  status?: ('live' | 'hidden')[];
  enabled_for_domain_ids?: number[];
  page?: number;
  limit?: number;
}

export interface ResourceBrowseResponse {
  count: number;
  data: Resource[];
  page?: number;
  limit?: number;
  total_pages?: number;
}

const buildTagFilterParams = (tagGroups?: number[], tagIds?: number[]): Record<string, string> => {
  const params: Record<string, string> = {};
  if (tagGroups && tagGroups.length > 0) {
    params.tag_groups = tagGroups.join(',');
  }
  if (tagIds && tagIds.length > 0) {
    params.tag_ids = tagIds.join(',');
  }
  return params;
};

const browseResources = async (params: ResourceBrowseParams): Promise<ResourceBrowseResponse> => {
  const admin_override = params.admin_override ?? true;

  if (params.search?.trim()) {
    const algoliaParams: any = {
      q: params.search.trim(),
      source_type: 'resources',
      admin_override,
      ...buildTagFilterParams(params.tag_groups, params.tag_ids),
      ...(params.domain_id !== undefined && { domain_id: params.domain_id }),
    };

    const response = await axiosGet('/simple_search/algolia/search', algoliaParams, 'v3');
    const data = response.data;

    return {
      count: data.count || data.data?.length || 0,
      data: data.data || [],
    };
  }

  const requestParams: any = {
    admin_override,
    ...buildTagFilterParams(params.tag_groups, params.tag_ids),
    ...(params.domain_id !== undefined && { domain_id: params.domain_id }),
    ...(params.client_id !== undefined && { client_id: params.client_id }),
    ...(params.status?.length && { status: params.status.join(',') }),
    ...(params.enabled_for_domain_ids?.length && {
      enabled_for_domain_ids: params.enabled_for_domain_ids.join(','),
    }),
    ...(params.page !== undefined && { page: params.page }),
    ...(params.limit !== undefined && { limit: params.limit }),
  };

  return await axiosGet('/resources/browse', requestParams, 'v3').then((response) => response.data);
};

export const useBrowseResources = (params: ResourceBrowseParams, options?: { enabled?: boolean }) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ResourceBrowseResponse>(
    ['resources_browse', params],
    () => browseResources(params),
    {
      enabled: options?.enabled ?? true,
      keepPreviousData: true,
    },
  );

  return { data, isLoading, error, refetch, isFetching };
};

export const useResources = (includeHidden: boolean, search?: string, clientId?: number, enabled?: boolean) => {
  const queryKey = [resourceQueryKeys.resources, clientId, includeHidden, search || ''];
  const queryOptions = enabled === undefined ? {} : { enabled };
  const { data, isLoading, error, refetch, isFetching } = useQuery<Resource[]>(
    queryKey,
    () => getResources(includeHidden, search, clientId),
    queryOptions,
  );
  return { data, isLoading, error, refetch, isFetching };
};

export const useResource = (resourceId: number) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<Resource>([resourceQueryKeys.resource], () =>
    getResource(resourceId),
  );
  return { data, isLoading, error, refetch, isFetching };
};

export const useUpdateResource = (resourceId: number) => {
  const queryClient = useQueryClient();
  const clientId = +localStorage.getItem('clientid')!;
  const updateResource = useMutation((data: any) => updateResourceRequest(resourceId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      queryClient.invalidateQueries([resourceQueryKeys.resources, clientId]);
      // Invalidate browse queries used in content library
      queryClient.invalidateQueries({ queryKey: ['resources_browse'] });
    },
    onError: (error) => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      queryClient.invalidateQueries({ queryKey: ['resources_browse'] });
      console.log(error);
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries(resourceQueryKeys.resources);
      await queryClient.cancelQueries({ queryKey: ['resources_browse'] });

      // Optimistic update for resource list
      queryClient.setQueryData(resourceQueryKeys.resources, (oldData: any) => {
        if (oldData) {
          return oldData.map((resource: Resource) => {
            if (resource.id === resourceId) {
              // When updating tags, clear tag_names to avoid stale data
              const updatedResource = { ...resource, ...data };
              if (data.tag_ids !== undefined) {
                updatedResource.tag_names = undefined;
                updatedResource.resource_tags = undefined;
              }
              return updatedResource;
            }
            return resource;
          });
        }
        return oldData;
      });

      // Optimistic update for resources_browse queries (content library)
      // Use getQueryCache to find all matching queries and update them
      const browseQueries = queryClient.getQueryCache().findAll({ queryKey: ['resources_browse'] });
      browseQueries.forEach((query) => {
        queryClient.setQueryData<ResourceBrowseResponse>(query.queryKey, (oldData) => {
          if (oldData?.data) {
            return {
              ...oldData,
              data: oldData.data.map((resource: Resource) => {
                if (resource.id === resourceId) {
                  // When updating tags, clear tag_names and resource_tags to avoid stale data
                  const updatedResource = { ...resource, ...data };
                  if (data.tag_ids !== undefined) {
                    updatedResource.tag_names = undefined;
                    updatedResource.resource_tags = undefined;
                  }
                  return updatedResource;
                }
                return resource;
              }),
            };
          }
          return oldData;
        });
      });
    },
  });

  return {
    updateResource,
  };
};

export const useAddResource = () => {
  const queryClient = useQueryClient();
  const clientId = +localStorage.getItem('clientid')!;
  const addResource = useMutation((data: any) => addResourceRequest(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      queryClient.invalidateQueries([resourceQueryKeys.resources, clientId]);
    },
    onError: (error) => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      console.log(error);
    },
    onMutate: (data) => {
      queryClient.setQueryData(resourceQueryKeys.resources, (oldData: any) => {
        if (oldData) {
          return [...oldData, data];
        }
        return oldData;
      });
    },
  });

  return {
    addResource,
  };
};

export const useAddResourceV3 = () => {
  const queryClient = useQueryClient();
  const clientId = +localStorage.getItem('clientid')!;
  const addResource = useMutation<Resource, Error, ResourceV3CreatePayload>((data) => addResourceV3Request(data), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      queryClient.invalidateQueries([resourceQueryKeys.resources, clientId]);
      queryClient.invalidateQueries({ queryKey: ['resources_browse'] });
      if (variables?.enable_for != null) {
        queryClient.invalidateQueries(clientQueryKeys.listClients);
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      console.log(error);
    },
    onMutate: (data) => {
      queryClient.setQueryData(resourceQueryKeys.resources, (oldData: Resource[] | undefined) => {
        if (oldData) {
          return [...oldData, { ...data, id: undefined } as Resource];
        }
        return oldData;
      });
    },
  });
  return { addResource };
};

export const useUpdateResourceV3 = (resourceId: number) => {
  const queryClient = useQueryClient();
  const clientId = +localStorage.getItem('clientid')!;
  const updateResource = useMutation<Resource, Error, ResourceV3UpdatePayload>(
    (data) => updateResourceV3Request(resourceId, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(resourceQueryKeys.resources);
        queryClient.invalidateQueries([resourceQueryKeys.resources, clientId]);
        queryClient.invalidateQueries({ queryKey: ['resources_browse'] });
        if (variables?.enable_for != null) {
          queryClient.invalidateQueries(clientQueryKeys.listClients);
        }
      },
      onError: (error) => {
        queryClient.invalidateQueries(resourceQueryKeys.resources);
        queryClient.invalidateQueries({ queryKey: ['resources_browse'] });
        console.log(error);
      },
      onMutate: async (data) => {
        await queryClient.cancelQueries(resourceQueryKeys.resources);
        await queryClient.cancelQueries({ queryKey: ['resources_browse'] });
        queryClient.setQueryData(resourceQueryKeys.resources, (oldData: Resource[] | undefined) => {
          if (oldData) {
            return oldData.map((resource) => {
              if (resource.id === resourceId) {
                const updatedResource = { ...resource, ...data } as Resource;
                if (data.tag_ids !== undefined) {
                  updatedResource.tag_names = undefined;
                  updatedResource.resource_tags = undefined;
                }
                return updatedResource;
              }
              return resource;
            });
          }
          return oldData;
        });
        const browseQueries = queryClient.getQueryCache().findAll({ queryKey: ['resources_browse'] });
        browseQueries.forEach((query) => {
          queryClient.setQueryData<ResourceBrowseResponse>(query.queryKey, (oldData) => {
            if (oldData?.data) {
              return {
                ...oldData,
                data: oldData.data.map((resource): Resource => {
                  if (resource.id === resourceId) {
                    const updatedResource = { ...resource, ...data } as Resource;
                    if (data.tag_ids !== undefined) {
                      updatedResource.tag_names = undefined;
                      updatedResource.resource_tags = undefined;
                    }
                    return updatedResource;
                  }
                  return resource;
                }),
              };
            }
            return oldData;
          });
        });
      },
    },
  );
  return { updateResource };
};

export const useRemoveResource = (resourceId: number) => {
  const queryClient = useQueryClient();
  const removeResource = useMutation(() => removeResourceRequest(resourceId), {
    onSuccess: () => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
    },
    onError: (error) => {
      queryClient.invalidateQueries(resourceQueryKeys.resources);
      console.log(error);
    },
    onMutate: () => {
      queryClient.setQueryData(resourceQueryKeys.resources, (oldData: any) => {
        if (oldData) {
          return oldData.filter((resource: Resource) => resource.id !== resourceId);
        }
        return oldData;
      });
    },
  });

  return {
    removeResource,
  };
};
